import { useEffect, useState } from 'react'
import { Card } from './Card'
import { TermsDocument } from './TermsDocument'
import { acceptTerms, loadTermsText, useStarOne, type TermsText } from '../lib/terms'
import { useHubLang } from '../lib/i18n'
import { TERMS } from '../lib/hubCopy'
import { messageOf } from '../lib/errors'

/**
 * 165 (David, 11 Oct): the Star One opt-in, shown "before they see any star
 * activity" — in place of the two star cards on the hub (variant 'card') and
 * in place of the wall and the Store on #/stars (variant 'page').
 *
 * Six points restate the terms David named (lib/hubCopy TERMS.starPoints); the
 * limit and expiry in them come from terms_text(), which reads star_policy, so
 * the card and the full terms cannot quote different numbers. The full terms
 * open in place. Joining is a ticked box and a button, then
 * StarOneContext.refresh() — every star surface under the gate redraws.
 *
 * Nothing here hides a recognition somebody else sent: the wall is Star One's
 * page and stays behind this, but stars given to the reader keep arriving in
 * their balance (the server never refuses stars ARRIVING).
 */
export function StarOneAgreement({ variant = 'card' }: { variant?: 'card' | 'page' }) {
  const lang = useHubLang()
  const t = TERMS[lang]
  const { refresh, previewPast } = useStarOne()
  const [doc, setDoc] = useState<TermsText | null>(null)
  const [locale, setLocale] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [open, setOpen] = useState(variant === 'page')
  const [ticked, setTicked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let live = true
    setLoadFailed(false)
    loadTermsText('star_one', locale ?? undefined)
      .then((d) => live && setDoc(d))
      .catch(() => live && setLoadFailed(true))
    return () => {
      live = false
    }
  }, [locale, attempt])

  async function join() {
    if (!doc || !ticked || busy) return
    setBusy(true)
    setError(null)
    try {
      await acceptTerms(doc.documentId, doc.locale)
      await refresh()
    } catch (e) {
      setError(`${t.saveFailed} (${messageOf(e)})`)
      setBusy(false)
    }
  }

  const limit = doc ? doc.balanceLimit.toLocaleString(lang === 'es' ? 'es' : undefined) : '—'

  return (
    <Card title={t.starTitle} className={`staragree staragree--${variant}`}>
      <p className="staragree__lede">{t.starLede}</p>

      {loadFailed ? (
        <>
          <p className="alert" role="alert">{t.loadFailed}</p>
          <button type="button" className="btn btn--quiet" onClick={() => setAttempt((n) => n + 1)}>
            {t.retry}
          </button>
        </>
      ) : !doc ? (
        <p className="empty" aria-busy="true">{t.loading}</p>
      ) : (
        <>
          <ul className="staragree__points">
            {t.starPoints(limit, doc.expiryMonths).map((pt) => <li key={pt}>{pt}</li>)}
          </ul>
          <p className="staragree__waiting">{t.starWaiting}</p>

          <button
            type="button"
            className="btn btn--link staragree__toggle"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? t.hideFull : t.readFull}
          </button>
          {open && (
            <div className="terms__scroll staragree__full" tabIndex={0}>
              <TermsDocument doc={doc} onSwitchLocale={setLocale} />
            </div>
          )}

          {previewPast ? (
            // 176. Viewing as somebody who has not joined: this is what they
            // see. Nobody can join for them; the preview can step past.
            <div className="terms__viewas">
              <p className="alert" role="note">
                They have not joined Star One yet, so this is what they see here. You cannot
                join for them.
              </p>
              <button type="button" className="btn btn--primary" onClick={previewPast}>
                Continue as if they had joined
              </button>
            </div>
          ) : (
            <>
              <label className="terms__agree">
                <input
                  type="checkbox"
                  checked={ticked}
                  onChange={(e) => setTicked(e.target.checked)}
                  disabled={busy}
                />
                <span>{t.agreeStarOne}</span>
              </label>
              {error && <p className="alert" role="alert">{error}</p>}
              <button
                type="button"
                className="btn btn--primary btn--block"
                disabled={!ticked || busy}
                onClick={() => void join()}
              >
                {t.join}
              </button>
            </>
          )}
        </>
      )}
    </Card>
  )
}
